import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check, Building2, Home, Info, Upload, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { IMAGE_ACCEPT } from "@/lib/storage";
import type { AdvertisementData } from "./types";

interface AdvertisementStepProps {
  data: AdvertisementData;
  onChange: (data: Partial<AdvertisementData>) => void;
  propertyImages?: string[] | null;
  unitImages?: string[] | null;
}

export function AdvertisementStep({ data, onChange, propertyImages, unitImages }: AdvertisementStepProps) {
  const { t } = useLanguage();

  // Combine property + unit images as available source images
  const allSourceImages = [
    ...(propertyImages || []),
    ...(unitImages || []),
  ];

  // Auto-populate existingImages on first load if empty
  useEffect(() => {
    if (data.existingImages.length === 0 && allSourceImages.length > 0) {
      onChange({ existingImages: [...allSourceImages] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyImages, unitImages]);

  const toggleImage = (url: string) => {
    const isSelected = data.existingImages.includes(url);
    if (isSelected) {
      onChange({ existingImages: data.existingImages.filter(img => img !== url) });
    } else {
      onChange({ existingImages: [...data.existingImages, url] });
    }
  };

  const selectAll = () => {
    onChange({ existingImages: [...allSourceImages] });
  };

  const deselectAll = () => {
    onChange({ existingImages: [] });
  };

  return (
    <div className="space-y-6">
      {/* REGA Info Banner */}
      <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 dark:bg-blue-950/20 dark:border-blue-800">
        <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-blue-700 dark:text-blue-400">
          {t(
            "بيانات العقار مستمدة من رخصة هيئة العقار. يمكنك فقط تعديل العنوان والوصف والصور.",
            "Property data is from the REGA license. You can only edit title, description, and images."
          )}
        </p>
      </div>

      {/* Title */}
      <div className="space-y-2">
        <Label>
          {t("عنوان الإعلان", "Title")} <span className="text-destructive">*</span>
        </Label>
        <Input
          value={data.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder={t("مثال: شقة فاخرة للإيجار في حي الياسمين", "Example: Luxury apartment for rent")}
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label>
          {t("الوصف", "Description")} <span className="text-destructive">*</span>
        </Label>
        <Textarea
          value={data.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder={t("اكتب وصفاً تفصيلياً للعقار...", "Write a detailed description...")}
          rows={4}
        />
      </div>

      {/* Images from Property & Unit */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>
            {t("صور العقار والوحدة", "Property & Unit Images")} <span className="text-destructive">*</span>
          </Label>
          {allSourceImages.length > 0 && (
            <div className="flex gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={selectAll} className="text-xs h-7">
                {t("تحديد الكل", "Select All")}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={deselectAll} className="text-xs h-7">
                {t("إلغاء الكل", "Deselect All")}
              </Button>
            </div>
          )}
        </div>

        {allSourceImages.length > 0 ? (
          <div className="space-y-3">
            {/* Property Images */}
            {(propertyImages || []).length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Building2 className="h-3 w-3" />
                  {t("صور العقار", "Property Images")}
                </p>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {(propertyImages || []).map((url, idx) => {
                    const isSelected = data.existingImages.includes(url);
                    return (
                      <button
                        key={`prop-${idx}`}
                        type="button"
                        onClick={() => toggleImage(url)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                          isSelected
                            ? 'border-primary ring-2 ring-primary/20'
                            : 'border-border opacity-50 hover:opacity-80'
                        }`}
                      >
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        {isSelected && (
                          <div className="absolute top-1 end-1 bg-primary text-primary-foreground rounded-full p-0.5">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Unit Images */}
            {(unitImages || []).length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Home className="h-3 w-3" />
                  {t("صور الوحدة", "Unit Images")}
                </p>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {(unitImages || []).map((url, idx) => {
                    const isSelected = data.existingImages.includes(url);
                    return (
                      <button
                        key={`unit-${idx}`}
                        type="button"
                        onClick={() => toggleImage(url)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                          isSelected
                            ? 'border-primary ring-2 ring-primary/20'
                            : 'border-border opacity-50 hover:opacity-80'
                        }`}
                      >
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        {isSelected && (
                          <div className="absolute top-1 end-1 bg-primary text-primary-foreground rounded-full p-0.5">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              {t(
                `تم تحديد ${data.existingImages.length} من ${allSourceImages.length} صورة`,
                `${data.existingImages.length} of ${allSourceImages.length} images selected`
              )}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* File upload dropzone */}
            <label className="border-2 border-dashed rounded-lg p-8 text-center text-muted-foreground cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors block">
              <Upload className="h-8 w-8 mx-auto mb-2" />
              <p className="text-sm font-medium">
                {t("ارفع صور العقار", "Upload property images")}
              </p>
              <p className="text-xs mt-1">
                {t("اختر صوراً من جهازك", "Select images from your device")}
              </p>
              <input
                type="file"
                accept={IMAGE_ACCEPT}
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 0) {
                    onChange({ images: [...data.images, ...files] });
                  }
                  e.target.value = "";
                }}
              />
            </label>

            {/* Preview uploaded files */}
            {data.images.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  {t(`${data.images.length} صورة مختارة`, `${data.images.length} images selected`)}
                </p>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {data.images.map((file, idx) => (
                    <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border-2 border-primary">
                      <img
                        src={URL.createObjectURL(file)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => onChange({ images: data.images.filter((_, i) => i !== idx) })}
                        className="absolute top-1 end-1 bg-destructive text-destructive-foreground rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
